import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { UtensilsCrossed } from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { HeroSimple } from '@/components/ui/HeroSimple'
import { WeeklyMenuSection } from '@/components/maia/WeeklyMenuSection'
import { StartersSection } from '@/components/maia/StartersSection'
import { DessertsSection } from '@/components/maia/DessertsSection'
import { AtmosphereSection } from '@/components/maia/AtmosphereSection'
import { RoomGallerySection } from '@/components/maia/RoomGallerySection'
import { Footer } from '@/components/layout/Footer'
import { LocalizedLink } from '@/components/LocalizedLink'
import { cn } from '@/lib/utils'
import { useWeeklyMenu } from '@/hooks/useWeeklyMenu'
import { useMenuItems } from '@/hooks/useMenuItems'
import type { MenuItem } from '@/components/maia/types'

const galleryImages = [
  { src: '/media/maia-sala-01.jpg', alt: 'La sala di Maia e Tas' },
  { src: '/media/maia-sala-02.jpg', alt: "Gli interni di Maia e Tas" },
  { src: '/media/maia-sala-03.jpg', alt: "L'atmosfera della sala di Maia e Tas" },
  { src: '/media/maia-sala-04.jpg', alt: 'Dettaglio della sala di Maia e Tas' },
]

export function Maia() {
  const { t, i18n } = useTranslation('maia')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const canonicalPath = `/${lang}/maia-e-tas`

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: 'Maia e Tas',
    image: 'https://maiaetas.it/media/logo-maia.jpg',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via Piacenza 97',
      addressLocality: 'Crema',
      addressRegion: 'CR',
      postalCode: '26013',
      addressCountry: 'IT',
    },
    telephone: '+39-333-702-4046',
    servesCuisine: 'Italian',
    priceRange: '€€',
    url: `https://maiaetas.it${canonicalPath}`,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '12:00',
        closes: '14:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '19:00',
        closes: '22:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '12:00',
        closes: '14:30',
      },
    ],
  }

  const staticPrimis = t('weeklyMenu.primis', { returnObjects: true }) as MenuItem[]
  const staticSeconds = t('weeklyMenu.seconds', { returnObjects: true }) as MenuItem[]
  const fallback = useMemo(
    () => ({ primis: staticPrimis, seconds: staticSeconds }),
    [staticPrimis, staticSeconds]
  )
  const menu = useWeeklyMenu(fallback)

  const staticStarters = t('starters.items', { returnObjects: true }) as MenuItem[]
  const staticDesserts = t('desserts.items', { returnObjects: true }) as MenuItem[]
  const starters = useMenuItems('starter', staticStarters)
  const desserts = useMenuItems('dessert', staticDesserts)

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
      />

      <main id="main-content">
        <HeroSimple
          backgroundColor="bg-maia"
          textColor="text-white"
          icon={UtensilsCrossed}
          title={t('hero.title')}
          ctas={[
            {
              label: t('hero.ctaBook'),
              href: 'book',
              variant: 'primary',
            },
          ]}
          className="min-h-[50vh] md:min-h-[55vh]"
        />

        {/* Atmosfera */}
        <AtmosphereSection
          title={t('atmosphere.title')}
          text={t('atmosphere.description')}
          imageSrc="/media/maia-atmosfera-2.jpg"
          imageAlt={t('atmosphere.imageAlt')}
          variant="light"
        />

        {/* Primi e secondi della settimana */}
        <WeeklyMenuSection
          id="weekly-menu"
          title={t('weeklyMenu.title')}
          description={t('weeklyMenu.description')}
          groups={[
            { title: t('weeklyMenu.primisTitle'), items: menu.primis },
            { title: t('weeklyMenu.secondsTitle'), items: menu.seconds },
          ]}
        />

        {/* Antipasti */}
        <StartersSection
          id="starters"
          title={t('starters.title')}
          description={t('starters.description')}
          items={starters}
        />

        {/* Dolci */}
        <DessertsSection
          id="desserts"
          title={t('desserts.title')}
          description={t('desserts.description')}
          items={desserts}
        />

        {/* Galleria */}
        <RoomGallerySection
          title={t('gallery.title')}
          text={t('gallery.description')}
          images={galleryImages}
        />

        {/* CTA finale */}
        <section className="bg-maia px-4 py-16 text-center sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-2xl">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-tight tracking-tight text-white">
              {t('finalCta.title')}
            </h2>
            {t('finalCta.subtitle') && (
              <p className="mt-4 text-lg text-white/90">{t('finalCta.subtitle')}</p>
            )}
            <LocalizedLink
              to="bar"
              className={cn(
                'mt-8 inline-flex items-center justify-center rounded-full bg-white px-8 py-3',
                'text-sm font-semibold text-maia transition-colors hover:bg-cream'
              )}
            >
              {t('finalCta.cta')}
            </LocalizedLink>
          </motion.div>
        </section>
      </main>

      <Footer variant="maia" />
    </>
  )
}
